






import { defineComponent, onMounted, onUnmounted, ref } from '@vue/composition-api';
import { event } from 'vue-gtag';
import TeaserText from '@/components/storyblok/TeaserText.vue';
import ImageCaption from '@/components/storyblok/ImageCaption.vue';
import ContentContainer from '@/components/storyblok/ContentContainer.vue';
import EduTitle from '@/components/storyblok/EduTitle.vue';
import RichText from '@/components/storyblok/RichText.vue';
import DownloadButton from '@/components/storyblok/DownloadButton.vue';

import TrackInfoHeading from '@/components/storyblok/track-info/Heading.vue';
import TrackInfoImage from '@/components/storyblok/track-info/Image.vue';
import TrackInfoText from '@/components/storyblok/track-info/Text.vue';
import TrackInfoVideo from '@/components/storyblok/track-info/Video.vue';

export default defineComponent({
  name: 'TrackInfoPage',
  props: {
    blok: { type: Object },
    analyticsPage: { type: String, default: '' },
  },
  components: { TeaserText, ImageCaption, ContentContainer, EduTitle, RichText, DownloadButton, TrackInfoHeading, TrackInfoImage, TrackInfoText, TrackInfoVideo },
  setup(props, ctx) {
    const trackInfoRoot = ref<HTMLElement | null>(null);
    const aboutPage = props.analyticsPage || ctx.root.$route.fullPath;
    const scrollMilestones = [25, 50, 75, 100];
    const reachedMilestones = new Set<number>();
    let scrollContainer: HTMLElement | null = null;
    let clickCount = 0;
    let maxScrollDepth = 0;
    let scrollTicking = false;

    function trackAboutEvent(eventName: string, params = {}) {
      event(eventName, {
        'event_category': 'about',
        'route': aboutPage,
        'about_page': aboutPage,
        'about_component': 'TrackInfoPage',
        ...params
      });
    }

    function getScrollDepth() {
      if (!scrollContainer) return 0;
      const maxScrollable = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      if (maxScrollable <= 0) return 100;
      const progress = (scrollContainer.scrollTop / maxScrollable) * 100;
      return Math.max(0, Math.min(100, Math.round(progress)));
    }

    function processScroll(trackMilestones = true) {
      const depth = getScrollDepth();
      maxScrollDepth = Math.max(maxScrollDepth, depth);

      if (!trackMilestones || !scrollContainer || scrollContainer.scrollTop <= 0) return;

      scrollMilestones.forEach((milestone) => {
        if (depth >= milestone && !reachedMilestones.has(milestone)) {
          reachedMilestones.add(milestone);
          trackAboutEvent('about_scroll_depth', {
            'event_label': `${milestone}%`,
            'value': milestone,
            'scroll_depth_percent': milestone
          });
        }
      });
    }

    function onScroll() {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(() => {
        processScroll();
        scrollTicking = false;
      });
    }

    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest('a, button, [role="button"]');
      if (!clickable) return;

      clickCount += 1;
      const clickableText = ((clickable.textContent || '').trim().replace(/\s+/g, ' ')).slice(0, 80);
      trackAboutEvent('about_click', {
        'event_label': clickableText || clickable.tagName.toLowerCase(),
        'value': clickCount,
        'about_click_total': clickCount
      });
    }

    onMounted(() => {
      trackAboutEvent('about_view');

      scrollContainer = trackInfoRoot.value ? (trackInfoRoot.value.closest('.view') as HTMLElement | null) : null;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', onScroll, { passive: true });
        processScroll(false);
      }
      if (trackInfoRoot.value) {
        trackInfoRoot.value.addEventListener('click', onClick, true);
      }
    });

    onUnmounted(() => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', onScroll);
      }
      if (trackInfoRoot.value) {
        trackInfoRoot.value.removeEventListener('click', onClick, true);
      }
      trackAboutEvent('about_leave', {
        'event_label': 'leave_about',
        'value': clickCount,
        'about_click_total': clickCount,
        'about_max_scroll_depth': maxScrollDepth
      });
    });

    return {
      trackInfoRoot,
    };
  },
});
